import type { Locale } from "@gioia365/i18n";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Il fuso del dispositivo, per le pagine senza l'azienda della scheda (l'invito, «Aziende di Gioia365»), le cui
 * risposte non portano il fuso di un'azienda: dentro un'azienda le date seguono il fuso dell'azienda, non questo.
 */
export function deviceTimeZone(): string {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

// Date e ore in numeri, giorno/mese/anno e ora (`24/09/2026 08:37`), in tutto il prodotto e in ogni lingua (decisione
// del 25 settembre 2026): elenchi, scheda, Timeline, Impostazioni, invito e «Aziende di Gioia365». Dentro un'azienda
// sempre nel suo fuso, mai in quello del browser; le due pagine senza azienda usano il fuso del dispositivo
// (`deviceTimeZone`). I formattatori costano: se ne tiene uno per fuso.
const numericFormatters = new Map<string, Intl.DateTimeFormat>();

function numericFormatterFor(timeZone: string): Intl.DateTimeFormat {
  let formatter = numericFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone,
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    numericFormatters.set(timeZone, formatter);
  }
  return formatter;
}

function numericParts(timeZone: string, iso: string): Partial<Record<Intl.DateTimeFormatPartTypes, string>> {
  const parts: Partial<Record<Intl.DateTimeFormatPartTypes, string>> = {};
  for (const part of numericFormatterFor(timeZone).formatToParts(new Date(iso))) parts[part.type] = part.value;
  return parts;
}

/** Un istante (`2026-09-24T06:37:00.000Z`) come `24/09/2026 08:37` nel fuso dato. */
export function formatNumericDateTime(timeZone: string, iso: string): string {
  const parts = numericParts(timeZone, iso);
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
}

/** L'ora di un istante come `08:37` nel fuso dato (gli orari della finestra «Richiama dopo»). */
export function formatNumericTime(timeZone: string, iso: string): string {
  const parts = numericParts(timeZone, iso);
  return `${parts.hour}:${parts.minute}`;
}

/** Il giorno di calendario di un istante nel fuso dato, come `2026-09-24` (il valore di un `<input type="date">`). */
export function localDayOf(timeZone: string, iso: string): string {
  const parts = numericParts(timeZone, iso);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Un giorno come `24/09/2026`: un istante nel fuso dato; una data di calendario (`2026-12-25`, per esempio una
 * chiusura) non ha fuso e resta quel giorno ovunque.
 */
export function formatNumericDate(timeZone: string, iso: string): string {
  if (DATE_ONLY.test(iso)) {
    const [year, month, day] = iso.split("-");
    return `${day}/${month}/${year}`;
  }
  const parts = numericParts(timeZone, iso);
  return `${parts.day}/${parts.month}/${parts.year}`;
}

/**
 * Una durata in ore, minuti e secondi come la scrivono i mock-up: «0:12», «6:12», «1:02:05». La durata di una telefonata
 * nella Timeline e la posizione dell'ascolto di una registrazione.
 */
export function formatClockDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, "0")}:${rest}` : `${minutes}:${rest}`;
}

const countFormatters = new Map<Locale, Intl.NumberFormat>();

/** Un conteggio con il separatore delle migliaia della lingua, anche a quattro cifre («1.234», «1,234»). */
export function formatCount(locale: Locale, value: number): string {
  let formatter = countFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { useGrouping: "always", maximumFractionDigits: 0 });
    countFormatters.set(locale, formatter);
  }
  return formatter.format(value);
}

/**
 * L'articolo italiano davanti a un numero dopo «tutti» (decisione P41): `"one"` per uno solo («l'unico risultato»),
 * `"gli"` quando il numero letto comincia con una vocale («tutti gli 8», «gli 11», «gli 80», «gli 800», «gli 8.000»,
 * «gli 11.500», «gli 80.000»; «gli un milione»), `"i"` altrimenti («tutti i 18», «i 100», «i 1.000», che si legge
 * «mille»). Conta la prima parte del numero letto, cioè il gruppo di tre cifre più a sinistra.
 */
export function italianCountArticle(value: number): "one" | "gli" | "i" {
  if (value === 1) return "one";
  let lead = Math.floor(Math.abs(value));
  let scale = 0;
  while (lead >= 1000) {
    lead = Math.floor(lead / 1000);
    scale += 1;
  }
  // «mille» comincia con una consonante; «un milione» e «un miliardo» con una vocale.
  if (lead === 1) return scale >= 2 ? "gli" : "i";
  return lead === 8 || lead === 11 || (lead >= 80 && lead <= 89) || (lead >= 800 && lead <= 899) ? "gli" : "i";
}

const euroFormatters = new Map<string, Intl.NumberFormat>();

/**
 * Un importo in centesimi di euro nel formato della lingua (`ACCEPTANCE_CRITERIA.md`, «Lingua e fuso orario»): «2.400 €»,
 * «€2,400»; i centesimi soltanto quando ci sono («2.400,50 €»). Le migliaia separate anche a quattro cifre, come i conteggi.
 */
export function formatEuroCents(locale: Locale, cents: number): string {
  const whole = cents % 100 === 0;
  const key = `${locale}:${whole ? 0 : 2}`;
  let formatter = euroFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", useGrouping: "always", minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 });
    euroFormatters.set(key, formatter);
  }
  return formatter.format(cents / 100);
}
